import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  Language,
  TranslationKey,
  Translations,
  loadTranslations
} from './locales';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>('en');
  const [translations, setTranslations] = useState<Translations | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initial language detection
  useEffect(() => {
    const initializeLanguage = async () => {
      // Check if user has saved preference in localStorage
      const savedLang = localStorage.getItem('language') as Language;

      let selectedLang: Language = 'en'; // default fallback

      if (savedLang && ['en', 'cs', 'de', 'fr', 'es', 'it', 'pl', 'pt', 'nl'].includes(savedLang)) {
        // Use saved preference
        selectedLang = savedLang;
      } else {
        // Detect browser language
        const browserLang = navigator.language.toLowerCase();

        // Map browser language codes to supported languages
        if (browserLang.startsWith('cs')) {
          selectedLang = 'cs';
        } else if (browserLang.startsWith('de')) {
          selectedLang = 'de';
        } else if (browserLang.startsWith('fr')) {
          selectedLang = 'fr';
        } else if (browserLang.startsWith('es')) {
          selectedLang = 'es';
        } else if (browserLang.startsWith('it')) {
          selectedLang = 'it';
        } else if (browserLang.startsWith('pl')) {
          selectedLang = 'pl';
        } else if (browserLang.startsWith('pt')) {
          selectedLang = 'pt';
        } else if (browserLang.startsWith('nl')) {
          selectedLang = 'nl';
        } else if (browserLang.startsWith('en')) {
          selectedLang = 'en';
        }
      }

      setLanguageState(selectedLang);

      // Load initial translations
      try {
        const loadedTranslations = await loadTranslations(selectedLang);
        setTranslations(loadedTranslations);
      } catch (error) {
        console.error('Failed to load translations:', error);
        // Fallback to English on error
        const fallbackTranslations = await loadTranslations('en');
        setTranslations(fallbackTranslations);
      } finally {
        setIsLoading(false);
      }
    };

    initializeLanguage();
  }, []);

  // Load translations when language changes
  useEffect(() => {
    if (!isLoading && language) {
      const loadNewTranslations = async () => {
        setIsLoading(true);
        try {
          const loadedTranslations = await loadTranslations(language);
          setTranslations(loadedTranslations);
        } catch (error) {
          console.error(`Failed to load translations for ${language}:`, error);
          // Keep current translations on error
        } finally {
          setIsLoading(false);
        }
      };

      loadNewTranslations();
    }
  }, [language]);

  // Keep <html lang> in sync so browsers don't offer (or auto-apply) machine
  // translation over our own translations (e.g. Chrome turning "Premium" into "Pojistné")
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  /**
   * Translation function with parameter interpolation
   *
   * @param key - Translation key from translations
   * @param params - Optional parameters to replace in the translation string
   *
   * @example
   * t('taskLimitMessage', { current: 45, limit: 50, count: 10 })
   * // Returns: "You have 45/50 tasks. This template has 10 tasks..."
   */
  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
    // Return key if translations not yet loaded
    if (!translations) {
      return key;
    }

    // Get translation from current language, fallback to key itself
    let text = translations[key] || key;

    // Replace parameters like {current}, {limit}, {count}
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
      });
    }

    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isLoading }}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Hook to access translation functions
 *
 * @returns { language, setLanguage, t }
 *
 * @example
 * const { t, language, setLanguage } = useTranslation();
 *
 * return (
 *   <button>{t('save')}</button>
 * );
 */
export function useTranslation() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}

/**
 * Hook to access language state
 * Alias for useTranslation for convenience
 */
export function useLanguage() {
  return useTranslation();
}
